import { getGigsV2ManagedListQueryOptions } from "@sidegigllc/api/client";
import { createRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { getTokenSilently } from "api/helpers";
import { gigQueryOptions } from "api/queries/stafferGigs";
import { env } from "config/env";
import { initializeAuth, sessionExists } from "domains/auth/initializeAuth";
import { captureNativeRedirect } from "domains/auth/queries/nativeRedirect";
import * as z from "zod/mini";
import { authedRoute } from "./authed";
import { rootRoute } from "./root";

// --- pre-auth (parented to rootRoute) ---

const interviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "interview/{-$secret}",
  component: lazyRouteComponent(() => import("views/InterviewLandingPage"), "InterviewLandingPage"),
});

const orientationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "orientation/{-$secret}",
  component: lazyRouteComponent(() => import("views/InterviewLandingPage"), "InterviewLandingPage"),
});

// `returnTo` is a pre-built path string read straight off the URL, so the redirect uses `href` — routing it
// through `to`/`search` would re-encode or mis-parse an already-complete path+query string.
export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "auth/$",
  loader: async ({ location }) => {
    initializeAuth();
    const urlParams = new URLSearchParams(location.searchStr);
    // Capture native-handoff mode before any redirect, so it is stored whether we bounce an already
    // signed-in user to the shell or fall through to render the login form.
    captureNativeRedirect(urlParams);
    // Gate on a real token in automation: `sessionExists()`'s unconditional automation bypass would
    // redirect a logged-out automation browser off the login flow into the authenticated shell.
    const authenticated = env.IS_AUTOMATION ? Boolean(getTokenSilently()) : await sessionExists();
    if (!authenticated) return;
    const returnTo = urlParams.get("returnTo") ?? "/";
    throw redirect({ href: returnTo });
  },
  component: lazyRouteComponent(() => import("domains/auth/views/Login"), "LoginView"),
});

// Kept as its own route (not folded into the login splat) so it ranks as the more specific match for
// anything under /auth/referral.
const referralRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "auth/referral/$",
  component: lazyRouteComponent(() => import("domains/auth/views/Referral"), "ReferralView"),
});

export const onboardingRootRoutes = [interviewRoute, orientationRoute, loginRoute, referralRoute];

// --- authed, pre-signup (parented to authedRoute) ---

export const teamCaptainRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "team-captain",
  validateSearch: z.object({ source_gig_id: z.optional(z.number()) }),
});

const teamCaptainIndexRoute = createRoute({
  getParentRoute: () => teamCaptainRoute,
  path: "/",
  loaderDeps: ({ search }) => ({ source_gig_id: search.source_gig_id }),
  loader: ({ deps, context }) => {
    void context.queryClient
      .query(getGigsV2ManagedListQueryOptions({ source_gig_id: deps.source_gig_id }))
      .catch(() => {});
  },
  component: lazyRouteComponent(() => import("views/TeamCaptain/ShiftList"), "ShiftList"),
});

export const teamCaptainShiftRoute = createRoute({
  getParentRoute: () => teamCaptainRoute,
  path: "$shiftId/roster",
  validateSearch: z.object({ name: z.optional(z.string()) }),
  loader: ({ params, context }) => {
    const shiftId = Number(params.shiftId);
    if (shiftId) void context.queryClient.query(gigQueryOptions(shiftId)).catch(() => {});
  },
  component: lazyRouteComponent(
    () => import("views/TeamCaptain/ShiftTimeAndAttendance"),
    "ShiftTimeAndAttendanceTeamCaptain",
  ),
});

const signUpRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "signup",
  validateSearch: z.object({
    ref: z.optional(z.string()),
    returnTo: z.optional(z.string()),
    skipAccountLinking: z.optional(z.boolean()),
  }),
  component: lazyRouteComponent(() => import("domains/signUp/views/SignUpPage"), "SignUpPage"),
});

const acceptOrgInvitationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "organization/invitations/accept/$token",
  component: lazyRouteComponent(
    () => import("domains/locations/containers/AcceptManagerInvite"),
    "AcceptManagerInviteContainer",
  ),
});

const acceptLocationInvitationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "invitations/accept/$token",
  component: lazyRouteComponent(
    () => import("domains/locations/containers/AcceptManagerInvite"),
    "AcceptManagerInviteContainer",
  ),
});

const accountLinkingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "account-linking",
  component: lazyRouteComponent(() => import("domains/signUp/containers/AccountLinking"), "AccountLinkingContainer"),
});

export const linkShiftOpsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "link/shiftops",
  validateSearch: z.object({ return: z.optional(z.string()) }),
  component: lazyRouteComponent(
    () => import("domains/integrations/shiftops/containers/LinkShiftOps"),
    "LinkShiftOpsContainer",
  ),
});

export const onboardingAuthedRoutes = [
  teamCaptainRoute.addChildren([teamCaptainIndexRoute, teamCaptainShiftRoute]),
  signUpRoute,
  acceptOrgInvitationRoute,
  acceptLocationInvitationRoute,
  accountLinkingRoute,
  linkShiftOpsRoute,
];
